import type { FC } from 'react';
import { useEffect, PropsWithChildren } from 'react';
import cn from 'classnames';
import { PapiUser } from '@wix/da-papi-types';
import DaApp from '@wix/da-shared-react/pkg/DaApp';
import SiteHeader from '@wix/da-shared-react/pkg/SiteHeader';
import { RuntimeEnvironment } from '@wix/da-shared-react/pkg/types';
import DrawerLoadable from '@wix/da-feedback-shared/pkg/Drawer/DrawerLoadable';
import FeedbackPageLoadable from '@wix/da-feedback-shared/pkg/FeedbackPage/FeedbackPageLoadable';
import { composedModalRenderer } from './modals';
import { submitNotificationRenderer } from './notifications';
import { MuroDepContext } from '@wix/da-react-context/pkg/MuroDepContext';
import MiniMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Mini';
import MicroMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Micro';
import MobileMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Mobile';
import { DndProvider } from 'react-dnd-cjs';
import HTML5Backend from 'react-dnd-html5-backend-cjs';
import LoadableSubmitModule from '@wix/da-submit-shared/pkg/redux/ModuleLoadable';
import { useUploadsBeforeUnload } from '../../hooks/useUploadsBeforeUnload';
import DamzDrawerLoadable from '@wix/da-damz-shared/pkg/components/Drawer/DrawerLoadable';
import DamzPageLoadable from '@wix/da-damz-shared/pkg/components/DamzPage/DamzPageLoadable';
import DamzGlobal from '@wix/da-damz-shared/pkg/redux/ModuleLoader';
import { damzModalRenderer } from '@wix/da-damz-shared/pkg/project-tools';
import { composeModalRenderers } from '@wix/da-shared-react/pkg/Modals/helpers';

const ShimDndProvider = DndProvider as any;
const MuroDepValue = {
  MicroComponent: MicroMuro,
  MiniComponent: MiniMuro,
  MobileComponent: MobileMuro,
};

import s from './App.scss';

export interface Props {
  currentUser?: PapiUser;
  language: string;
  environment?: RuntimeEnvironment;
  appLoaded: () => void;
  bodyClassName?: string;
}

const App: FC<PropsWithChildren<Props>> = ({
  appLoaded,
  language,
  environment,
  bodyClassName,
  children,
}) => {
  useUploadsBeforeUnload();
  useEffect(
    () => {
      appLoaded();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <MuroDepContext.Provider value={MuroDepValue}>
      <DaApp
        language={language}
        environment={environment}
        bodyClassName={cn(s['root'], bodyClassName)}
        modalsRenderFunc={composeModalRenderers(
          composedModalRenderer,
          damzModalRenderer as any
        )}
        notificationsRenderFunc={submitNotificationRenderer}
        ncContextProps={{
          PageComponent: FeedbackPageLoadable,
          DrawerComponent: DrawerLoadable,
        }}
        damzContextProps={{
          GlobalComponent: DamzGlobal,
          DrawerComponent: DamzDrawerLoadable,
          PageComponent: DamzPageLoadable,
        }}
        muroDepValue={MuroDepValue}
      >
        <LoadableSubmitModule />
        <SiteHeader />
        <ShimDndProvider backend={HTML5Backend}>{children}</ShimDndProvider>
      </DaApp>
    </MuroDepContext.Provider>
  );
};
App.displayName = 'App';

export default App;
